import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import getBlogPosts from 'src/queries/getBlogPosts.graphql';
import camelcaseKeys from 'camelcase-keys';
import { Link } from 'react-router';
import Bugsnag from '@bugsnag/js';
import { BlogPostsLoading } from 'src/components/Blog/BlogPostsLoading';
import { BlogPosts as BlogPostsType } from 'src/types/global-types';
import { useHeadTags, escapeAttr } from 'src/core/engine/head';
import clientStoreConfigQuery from 'src/queries/clientStoreConfig.graphql';
import { clientStoreConfig } from 'src/queries/__generated__/clientStoreConfig';

import classes from './BlogPosts.scss';

type Props = {
    page: number;
    numberOfResultsPerPage: number;
};

const useStoreConfigMetadata = () => {
    const { error, data } = useQuery<clientStoreConfig>(clientStoreConfigQuery, { ssr: false });

    return useMemo(() => {
        if (!data) return undefined;
        if (error) {
            Bugsnag.notify(`Error: ${error}`);
            return undefined;
        }
        const camelizedData = camelcaseKeys(data, { deep: true });
        if (!camelizedData.storeConfig?.blogMetaTitle) return undefined;
        return {
            metaTitle: camelizedData.storeConfig.blogMetaTitle as string,
            metaDescription: camelizedData.storeConfig.blogMetaDescription as string | undefined,
            mfblogIndexPageTitle: camelizedData.storeConfig.mfblogIndexPageTitle as string | undefined,
        };
    }, [data, error]);
};

export const BlogPosts: React.FC<Props> = ({ page, numberOfResultsPerPage }) => {
    const { setHead } = useHeadTags();
    const storeConfigMetadata = useStoreConfigMetadata();
    const { error, data, loading } = useQuery(getBlogPosts, {
        variables: {
            currentPage: page,
            pageSize: numberOfResultsPerPage,
        },
    });

    const metaTitle = storeConfigMetadata?.metaTitle;
    const metaDescription = storeConfigMetadata?.metaDescription;

    useEffect(() => {
        if (!metaTitle && !metaDescription) return;
        const title = metaTitle ? `<title>${escapeAttr(metaTitle)}</title>` : '';
        const meta = metaDescription ? `<meta name="description" content="${escapeAttr(metaDescription)}" />` : '';
        setHead({ title: title || undefined, meta: meta || undefined });
    }, [metaTitle, metaDescription, setHead]);

    if (loading) return <BlogPostsLoading />;

    if (!data || error) {
        Bugsnag.notify(`Error: ${error}`);
        return null;
    }

    const camelizedData = camelcaseKeys(data, { deep: true }) as BlogPostsType;

    return (
        <div className={classes.wrapper}>
            <h1>{storeConfigMetadata?.mfblogIndexPageTitle}</h1>
            <div className={classes.container}>
                {camelizedData.blogPosts.items.map((post) => (
                    <div className={classes.postContainer} key={post.postId}>
                        <div className={classes.post} style={{ backgroundImage: `url(${post.featuredImage})` }}>
                            <Link to={`/blog/${post.identifier}`} key={post.postId}>
                                <div className={classes.content}>
                                    {post.tags && (
                                        <div className={classes.tags}>
                                            {post.tags.map((tag) => (
                                                <p key={tag.tagId} className={classes.tag}>
                                                    {tag.title}
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                    <p className={classes.title}>{post.title}</p>
                                </div>
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
